import React, { useState, useEffect } from 'react';
import { Select, Row, Col } from 'antd';
import { useAuth } from '../contexts/AuthContext';

const { Option } = Select;

const CustomerSelector = ({ 
  value, 
  onChange, 
  disabled = false, 
  allowClear = true, 
  placeholder = '请选择校区',
  onCustomerChange = null,
  classIdValue = null,
  onClassChange = null,
  classList = [],
  classDisabled = false,
  showTeamSelector = true, // 是否展示小队选择器，默认为true
  fetchClassList = null // 外部提供的获取小队列表的函数
}) => {
  const { apiClient } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // 同步外部传入的classIdValue
  const [internalClassIdValue, setInternalClassIdValue] = useState(classIdValue);
  
  useEffect(() => {
    setInternalClassIdValue(classIdValue);
  }, [classIdValue]);
  
  // 获取当前用户信息
  const getCurrentUser = () => {
    try {
      const userStr = localStorage.getItem('user');
      return userStr ? JSON.parse(userStr) : null;
    } catch (e) {
      console.error('解析用户信息失败:', e);
      return null;
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    if (!apiClient) return;
    
    setLoading(true);
    try {
      const response = await apiClient.get('/sports-base-server-prod/myPage/searchCustomer', {
        params: { pageNum: 1, pageSize: 999 }
      });
      
      if (response.data.result === '1') {
        const fullCustomerList = response.data.customerList || [];        
        // 更新 localStorage 中的 customerList，包含 phoneDecimal
        try {
          const simplifiedCustomerList = fullCustomerList.map(c => ({
            customerId: c.customerId,
            customerName: c.customerName,
            phoneDecimal: c.phoneDecimal // 包含 phoneDecimal 信息
          }));
          localStorage.setItem('customerList', JSON.stringify(simplifiedCustomerList));
        } catch (err) {
          console.error('保存校区列表到 localStorage 失败:', err);
        }
        
        // 根据权限过滤校区列表
        const currentUser = getCurrentUser();
        if (!currentUser) {
          setCustomers(fullCustomerList);
          return;
        }
        
        let filteredCustomers = [];
        if (currentUser.role === 'R00001') {
          // 角色为 R00001 时，可以看到所有校区
          filteredCustomers = fullCustomerList;
        } else {
          // 检查当前用户是否在任何校区的 phoneDecimal 中，或者 customerId 是否匹配
          filteredCustomers = fullCustomerList.filter(customer => {
            // 检查 customerId 是否匹配当前用户
            if (customer.customerId === currentUser.customerId) {
              return true;
            }
            
            // 检查当前用户的各种可能标识是否在 phoneDecimal 中 (精确匹配)
            if (customer.phoneDecimal) {
              const phoneDecimals = customer.phoneDecimal.split(';').map(item => item.trim()).filter(item => item);
              
              // 检查多种可能的用户标识
              const userIdentifiers = [
                currentUser.userName,     // 用户名
                currentUser.name,         // 姓名
                currentUser.userId,       // 用户ID
                currentUser.username,     // 登录用户名
              ].filter(id => id); // 过滤掉undefined或null值
                
              // 检查是否有任何一个用户标识存在于phoneDecimal中
              for (const identifier of userIdentifiers) {
                if (phoneDecimals.includes(identifier.toString())) {
                  return true;
                }
              }
            }
            
            return false;
          });
        }
        
        setCustomers(filteredCustomers);
      } else {
        console.warn('获取校区列表返回异常或为空:', response?.data);
        setCustomers([]);
      }
    } catch (error) {
      console.error('获取校区列表错误:', error);
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerChange = (customerId) => {
    if (onChange) {
      onChange(customerId);
    }
    
    // 如果提供了 onCustomerChange 回调，也执行它
    if (onCustomerChange) {
      onCustomerChange(customerId);
      // 同时清空班级选择
      if (onClassChange) {
        onClassChange(undefined);
      }
      
      // 如果提供了外部的获取班级列表函数，则使用它
      if (fetchClassList && typeof fetchClassList === 'function') {
        fetchClassList(customerId);
      }
    }
  };

  const handleClassChange = (classId) => {
    setInternalClassIdValue(classId);
    if (onClassChange) {
      // 查找对应的className
      const selectedClass = classList.find(c => c.classId === classId);
      const className = selectedClass ? selectedClass.className : null;
      onClassChange(classId, className);
    }
  };

  if (showTeamSelector) {
    return (
      <div>
        <Row gutter={8} align="middle">
          <Col span={18}>
            <Row gutter={8} align="middle">
              <Col span={14}>
                <Select
                  value={value}
                  onChange={handleCustomerChange}
                  placeholder={placeholder}
                  allowClear={allowClear}
                  disabled={loading}
                  loading={loading}
                  style={{ width: '100%' }}
                >
                  {customers.map(c => (
                    <Option key={c.customerId} value={c.customerId}>
                      {c.customerName}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col span={10}>
                <Select
                  value={internalClassIdValue}
                  onChange={handleClassChange}
                  placeholder="请选择小队"
                  allowClear={true}
                  disabled={classDisabled || !value}
                  style={{ width: '100%' }}
                >
                  {classList.map(c => (
                    <Option key={c.classId} value={c.classId}>
                      {c.className}
                    </Option>
                  ))}
                </Select>
              </Col>
            </Row>
          </Col>
        </Row>
      </div>
    );
  } else {
    return (
      <Select
        value={value}
        onChange={handleCustomerChange}
        placeholder={placeholder}
        allowClear={allowClear}
        disabled={loading}
        loading={loading}
        style={{ minWidth: 300 }}
      >
        {customers.map(c => (
          <Option key={c.customerId} value={c.customerId}>
            {c.customerName}
          </Option>
        ))}
      </Select>
    );
  }
};

export default CustomerSelector;